import { useEffect, useState } from "react";
import { ApiClientError, fetchMallAds } from "../api/client";
import { StateBanner } from "../components/StateBanner";
import { yen } from "../lib/meta";
import { BidControlSection } from "./mall-ads/BidControlSection";
import type { MallAdsResponse, RppMetrics, RppReportRow } from "../types/contract";

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: MallAdsResponse };

type RowKind = "products" | "keywords";

function formatMD(dateStr: string): string {
  const [, m, d] = dateStr.split("-");
  return `${Number(m)}/${Number(d)}`;
}

// 契約上、roas/cvrは楽天RPPレポート表示に合わせた%値としてAPIから届く
// （倍率(x)や小数(0.0141)ではなく、456.14や1.41のような数値。Lead通知2026-08-25）。
function pctValue(v: number): string {
  return `${v.toFixed(2)}%`;
}

function cpcValue(v: number): string {
  return `¥${v.toFixed(1)}`;
}

// cpc/roas/cvr/cpa は分母が0のとき null で届く（例: 月初でRPP経由の売上がまだ0件→cpa=null）。
// 0円/0%と誤表示しないよう「—」にする。
function orDash(v: number | null, format: (v: number) => string): string {
  return v === null ? "—" : format(v);
}

const KPI_TILES: Array<{ key: keyof RppMetrics; label: string; format: (v: number) => string }> = [
  { key: "clicks", label: "クリック数", format: (v) => `${v.toLocaleString("ja-JP")}回` },
  { key: "spend", label: "実績額", format: yen },
  { key: "cpc", label: "CPC", format: cpcValue },
  { key: "sales_amount", label: "売上金額", format: yen },
  { key: "roas", label: "ROAS", format: pctValue },
  { key: "sales_count", label: "売上件数", format: (v) => `${v.toLocaleString("ja-JP")}件` },
  { key: "cvr", label: "CVR", format: pctValue },
  { key: "cpa", label: "注文獲得単価", format: yen },
];

export function MallAdsPage() {
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [rowKind, setRowKind] = useState<RowKind>("products");

  const load = () => {
    setState({ status: "loading" });
    fetchMallAds()
      .then((data) => setState({ status: "ok", data }))
      .catch((e: unknown) => {
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setState({ status: "error", message });
      });
  };

  useEffect(load, []);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">モール広告（楽天RPP）</h1>
      </div>

      {state.status === "loading" && <SkeletonTiles />}
      {state.status === "error" && <StateBanner kind="error" message={state.message} onRetry={load} />}

      {state.status === "ok" && state.data.snapshot === null && (
        <div className="card">
          <div className="empty-state">データ未取得（スクレイパー稼働後に表示されます）</div>
        </div>
      )}

      {state.status === "ok" && state.data.snapshot !== null && (
        <MallAdsBody
          snapshot={state.data.snapshot}
          products={state.data.products}
          keywords={state.data.keywords}
          rowKind={rowKind}
          onRowKindChange={setRowKind}
        />
      )}

      <BidControlSection />
    </div>
  );
}

function MallAdsBody({
  snapshot,
  products,
  keywords,
  rowKind,
  onRowKindChange,
}: {
  snapshot: NonNullable<MallAdsResponse["snapshot"]>;
  products: RppReportRow[];
  keywords: RppReportRow[];
  rowKind: RowKind;
  onRowKindChange: (k: RowKind) => void;
}) {
  const rows: RppReportRow[] = rowKind === "products" ? products : keywords;

  return (
    <>
      <div className="mall-ads-period">
        {formatMD(snapshot.period_from)}〜{formatMD(snapshot.period_to)}（前日まで・CV計測720時間）
      </div>

      <div className="kpi-grid">
        {KPI_TILES.map((tile) => (
          <div className="kpi-card" key={tile.key}>
            <div className="kpi-label">{tile.label}</div>
            <div className="kpi-value">{orDash(snapshot.totals[tile.key], tile.format)}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="tab-row" role="tablist" style={{ marginBottom: 12 }}>
          <button
            role="tab"
            aria-selected={rowKind === "products"}
            className={`tab-btn ${rowKind === "products" ? "active" : ""}`}
            onClick={() => onRowKindChange("products")}
          >
            商品別
          </button>
          <button
            role="tab"
            aria-selected={rowKind === "keywords"}
            className={`tab-btn ${rowKind === "keywords" ? "active" : ""}`}
            onClick={() => onRowKindChange("keywords")}
          >
            キーワード別
          </button>
        </div>

        {rows.length === 0 ? (
          <div className="empty-state">
            {rowKind === "products" ? "商品別データがありません。" : "キーワード別データがありません。"}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{rowKind === "products" ? "商品" : "キーワード"}</th>
                  <th>実績額</th>
                  <th>売上金額</th>
                  <th>ROAS</th>
                  <th>クリック数</th>
                  <th>CPC</th>
                  <th>売上件数</th>
                  <th>CVR</th>
                  <th>CPA</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td style={{ whiteSpace: "normal", minWidth: 220, wordBreak: "break-all" }}>{r.name}</td>
                    <td>{yen(r.spend)}</td>
                    <td>{yen(r.sales_amount)}</td>
                    <td>{orDash(r.roas, pctValue)}</td>
                    <td>{r.clicks.toLocaleString("ja-JP")}</td>
                    <td>{orDash(r.cpc, cpcValue)}</td>
                    <td>{r.sales_count.toLocaleString("ja-JP")}件</td>
                    <td>{orDash(r.cvr, pctValue)}</td>
                    <td>{orDash(r.cpa, yen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function SkeletonTiles() {
  return (
    <div className="kpi-grid">
      {Array.from({ length: 8 }).map((_, i) => (
        <div className="kpi-card" key={i}>
          <div className="skeleton" style={{ height: 12, width: "60%", marginBottom: 10 }} />
          <div className="skeleton" style={{ height: 22, width: "80%" }} />
        </div>
      ))}
    </div>
  );
}
